<script setup lang="ts">
// Interactive demo for the "Editor" landing section: a miniature of the real
// editor (/customize). Left is Ava's page in edit mode (inline-editable name
// and location with pencil icons, dashed manage buttons, as DefaultLayout
// renders them); right is the Builder sidebar with the real controls
// (ColorInput, sliders, the background grid, the font select). Every change
// restyles the page live. The sidebar is always dark like the product; the
// page shows Ava's light or dark theme with the site colour mode.

const FONTS = [
  { label: 'Space Grotesk', value: 'Space Grotesk', class: 'font-space-grotesk' },
  { label: 'Rajdhani', value: 'Rajdhani', class: 'font-rajdhani' },
  { label: 'Orbitron', value: 'Orbitron', class: 'font-orbitron' },
  { label: 'Chakra Petch', value: 'Chakra Petch', class: 'font-chakra-petch' },
  { label: 'Teko', value: 'Teko', class: 'font-teko' }
]

const BACKGROUNDS = ['page-bg', 'page-bg-2', 'page-bg-3']

const sections = ref('theme')
const accordionItems = [
  { label: 'Theme', icon: 'i-lucide-palette', value: 'theme', slot: 'theme' as const },
  { label: 'Background', icon: 'i-lucide-wallpaper', value: 'background', slot: 'background' as const },
  { label: 'Text', icon: 'i-lucide-type', value: 'text', slot: 'text' as const }
]

const accent = ref('#8b5cf6')
const cardOpacity = ref(55)
const cardBlur = ref(12)
const font = ref('Space Grotesk')
const background = ref('page-bg')
const displayName = ref('Ava')
const location = ref('Lisbon')

const dirty = ref(false)
const justSaved = ref(false)
watch([accent, cardOpacity, cardBlur, font, background, displayName, location], () => {
  dirty.value = true
  justSaved.value = false
})

function save() {
  dirty.value = false
  justSaved.value = true
}

const fontClass = computed(() => FONTS.find(f => f.value === font.value)?.class ?? 'font-space-grotesk')
const accentRgb = computed(() => hexToRgb(accent.value).join(', '))

// The page overrides the accent the way a real page overrides its primary colour
const pageVars = computed(() => ({
  '--mock-accent': accent.value,
  '--mock-accent-rgb': accentRgb.value
}))

// Real card style (useLayoutStyles cardStyle) on the mode's card colour
const cardStyle = computed(() => ({
  background: `radial-gradient(ellipse 100% 80% at 50% -10%, rgba(${accentRgb.value}, 0.08) 0%, transparent 50%), rgba(var(--mock-glass-rgb), ${cardOpacity.value / 100})`,
  backdropFilter: cardBlur.value > 0 ? `blur(${cardBlur.value}px)` : 'none'
}))

const nameStyle = computed(() => ({
  fontSize: '2.5rem',
  fontWeight: 500,
  lineHeight: 1.2,
  color: 'var(--mock-text-strong)'
}))

const locationStyle = { fontSize: '0.875rem', color: 'var(--mock-text-dim)' }

const badges = [
  { icon: 'i-lucide-crown', color: '#fbbf24' },
  { icon: 'i-lucide-gem', color: '#e879f9' },
  { icon: 'i-lucide-heart', color: '#fb7185' }
]

const links = [
  { icon: 'i-simple-icons-youtube', color: '#FF0000' },
  { icon: 'i-simple-icons-tiktok', color: null },
  { icon: 'i-simple-icons-instagram', color: '#E4405F' },
  { icon: 'i-simple-icons-spotify', color: '#1DB954' }
]
</script>

<template>
  <div class="w-full rounded-2xl border border-default bg-elevated/30 p-2 shadow-(--surface-shadow) sm:p-2.5">
    <div class="flex flex-col overflow-hidden rounded-xl border border-default sm:h-115 sm:flex-row">
      <!-- The page, in edit mode -->
      <div
        class="mock-page relative isolate flex min-h-90 flex-1 items-center justify-center overflow-hidden px-4 py-8"
        :class="fontClass"
        :style="pageVars"
      >
        <MockPhoto
          :key="background"
          :name="background"
          class="bg-fade -z-10"
        />
        <div class="absolute inset-0 -z-10 bg-(--mock-page-dim)" />

        <div
          class="mock-card w-full max-w-lg p-8 text-center [zoom:0.66]"
          :style="cardStyle"
        >
          <div class="mb-6 flex justify-center">
            <span class="relative block size-24 overflow-hidden rounded-full ring-2 ring-primary-400/30">
              <MockPhoto
                name="avatar-ava"
                single
              />
            </span>
          </div>

          <div class="mb-1 flex flex-wrap items-center justify-center gap-2">
            <InlineEditText
              v-model="displayName"
              editable
              tag="h1"
              placeholder="ava"
              :display-style="nameStyle"
            />
            <div class="flex items-center gap-0.5">
              <span
                v-for="badge in badges"
                :key="badge.icon"
                class="flex items-center justify-center rounded-full bg-primary-500/10 px-1.5 py-1"
              >
                <UIcon
                  :name="badge.icon"
                  class="size-4"
                  :style="{ color: badge.color }"
                />
              </span>
              <span class="ml-1 flex size-7 items-center justify-center rounded-full border-2 border-dashed border-(--mock-card-border) text-(--mock-text-dim)">
                <UIcon
                  name="i-lucide-settings"
                  class="size-3"
                />
              </span>
            </div>
          </div>

          <div
            class="mb-6 mt-3 flex items-center justify-center gap-1.5"
            :style="locationStyle"
          >
            <UIcon
              name="i-lucide-map-pin"
              class="size-3.5 shrink-0"
            />
            <InlineEditText
              v-model="location"
              editable
              placeholder="Add location"
              :display-style="locationStyle"
            />
          </div>

          <div class="mx-auto mb-6 mt-4 h-px max-w-xs bg-[rgba(var(--mock-accent-rgb),0.3)]" />

          <p class="mx-auto max-w-md text-lg text-(--mock-text-muted)">
            Songwriter and streamer. New EP out now.
          </p>

          <div class="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span
              v-for="link in links"
              :key="link.icon"
              class="flex size-10 items-center justify-center rounded-xl"
            >
              <UIcon
                :name="link.icon"
                class="size-5"
                :class="link.color ? '' : 'text-(--mock-text-strong)'"
                :style="link.color ? { color: link.color } : undefined"
              />
            </span>
          </div>
          <div class="mt-3 flex justify-center">
            <span class="flex size-10 items-center justify-center rounded-xl border-2 border-dashed border-(--mock-card-border) text-(--mock-text-dim)">
              <UIcon
                name="i-lucide-settings"
                class="size-4"
              />
            </span>
          </div>
        </div>
      </div>

      <!-- Builder sidebar (always dark, like the real editor) -->
      <aside class="builder-panel dark flex w-full shrink-0 flex-col border-t border-white/8 text-white sm:w-64 sm:border-l sm:border-t-0">
        <div class="flex items-center justify-between gap-2 border-b border-white/8 px-4 py-3">
          <div class="flex items-center gap-2.5">
            <div class="flex size-7 items-center justify-center rounded-lg border border-primary-400/25 bg-primary-500/15">
              <UIcon
                name="i-lucide-paintbrush"
                class="size-3.5 text-primary-400"
              />
            </div>
            <span class="text-sm font-semibold text-white/90">Builder</span>
          </div>
          <UButton
            :disabled="!dirty"
            size="xs"
            color="primary"
            variant="solid"
            :icon="justSaved ? 'i-lucide-check' : 'i-lucide-save'"
            :label="justSaved ? 'Saved' : 'Save'"
            @click="save"
          />
        </div>

        <div class="flex-1 overflow-y-auto px-1 py-1">
          <UAccordion
            v-model="sections"
            :items="accordionItems"
            type="single"
            collapsible
            :ui="{ trigger: 'px-3 py-2.5', content: 'px-2 pt-1' }"
          >
            <template #theme-body>
              <div class="space-y-3 px-1 pb-2">
                <div class="space-y-1.5">
                  <label class="text-xs font-medium text-white/50">Primary Color</label>
                  <p class="text-[10px] text-white/25">
                    Buttons, accents, and player controls
                  </p>
                  <ColorInput v-model="accent" />
                </div>
                <div class="space-y-1.5">
                  <div class="flex items-center justify-between">
                    <label class="text-xs font-medium text-white/50">Card Opacity</label>
                    <span class="text-xs tabular-nums text-white/30">{{ cardOpacity }}%</span>
                  </div>
                  <USlider
                    v-model="cardOpacity"
                    :min="0"
                    :max="100"
                    :step="1"
                    size="xs"
                    aria-label="Card opacity"
                  />
                </div>
                <div class="space-y-1.5">
                  <div class="flex items-center justify-between">
                    <label class="text-xs font-medium text-white/50">Card Blur</label>
                    <span class="text-xs tabular-nums text-white/30">{{ cardBlur }}px</span>
                  </div>
                  <USlider
                    v-model="cardBlur"
                    :min="0"
                    :max="50"
                    :step="1"
                    size="xs"
                    aria-label="Card blur"
                  />
                </div>
              </div>
            </template>

            <template #background-body>
              <div class="grid grid-cols-3 gap-2.5 p-1 pb-2">
                <button
                  v-for="(bg, i) in BACKGROUNDS"
                  :key="bg"
                  type="button"
                  class="relative aspect-video cursor-pointer overflow-hidden rounded-lg border-2 transition-all duration-200"
                  :class="bg === background ? 'border-primary-400 ring-1 ring-primary-400/30' : 'border-white/8 hover:border-white/20'"
                  :aria-label="`Background ${i + 1}`"
                  @click="background = bg"
                >
                  <MockPhoto :name="`${bg}-thumb`" />
                </button>
              </div>
            </template>

            <template #text-body>
              <div class="space-y-1.5 px-1 pb-2">
                <label class="block text-xs font-medium text-white/50">Font Family</label>
                <USelect
                  v-model="font"
                  :items="FONTS"
                  value-key="value"
                  size="sm"
                  class="w-full"
                  :ui="{ content: 'dark' }"
                  aria-label="Font family"
                />
              </div>
            </template>
          </UAccordion>
        </div>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.bg-fade {
  animation: bg-fade 0.4s ease-out;
}

@keyframes bg-fade {
  from {
    opacity: 0;
  }
}

/* Same panel background as the real BuilderSidebar */
.builder-panel {
  background:
    radial-gradient(ellipse 120% 50% at 100% -10%, rgba(61, 90, 168, 0.1) 0%, transparent 60%),
    linear-gradient(180deg, rgba(10, 14, 42, 0.95) 0%, rgba(3, 2, 10, 0.98) 100%);
}
</style>
